import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
/**
 * Every published page, and whether anything has happened since it went live.
 *
 * The reports screen beside this one is the honest one and therefore the empty
 * one: a page appears on it only once it has been live fourteen days and the
 * move has cleared the significance floor, which most pages never do. That
 * refusal is the product, and it leaves a real question unanswered — did this
 * page do *anything* — which is what this screen is for.
 *
 * So the two columns here are counts and are printed as counts. Whether the
 * difference between them means anything is what a reflow report says, and it
 * is linked from the row rather than guessed at here. A page with no report yet
 * says how many days are left instead of showing an arrow.
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { api, type PageStanding } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, SortHeader, TableTools, useSort } from "~/modules/analytics/components/table";
import { readingPath } from "~/modules/analytics/lib/loop";

/** The wait a reflow keeps, copied from `loops::reflow` so the row can say how
 *  much of it is left rather than leaving somebody wondering. */
const REFLOW_DAYS = 14;

type Column = "title" | "live" | "before" | "since";

export function ReflowPagesPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const [query, setQuery] = useState("");
	const sort = useSort<Column>("live", "asc");

	const state = useQuery({
		queryKey: [...keys.entity(entityId), "reflow-pages", { range: filters.range, locale: filters.locale }],
		queryFn: () => api.reflowPages(entityId, filters),
		...read,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="since"
			lede={t("reflowPages.lede", { days: REFLOW_DAYS })}
			filters={filters}
			setFilters={setFilters}
		>
			<Async
				state={state}
				empty={
					<EmptyState
						size="compact"
						title={t("reflowPages.empty")}
						description={t("reflowPages.emptyBody")}
						action={<DrillLink to={readingPath(brand(entityId), "published")}>{t("empty.goPublished")}</DrillLink>}
					/>
				}
			>
				{(all) => {
					const rows = sort.apply(
						all.filter((row) => matches(query, row.title, row.kind)),
						(row, column) =>
							column === "live"
								? row.days_live
								: column === "before"
									? row.cited_before
									: column === "since"
										? row.cited_since
										: row.title,
					);

					return (
						<>
							<TableTools
								sort={sort.config([
									{ id: "title", label: t("table.page") },
									{ id: "live", label: t("reflowPages.live") },
									{ id: "before", label: t("reflowPages.before") },
									{ id: "since", label: t("reflowPages.since") },
								])}
								query={query}
								onQuery={setQuery}
								placeholder={t("table.title")}
								shown={rows.length}
								total={all.length}
								csv={{ name: "published-pages", rows: csv(all) }}
							/>

							{rows.length === 0 ? (
								<EmptyState size="compact" title={t("table.noMatch")} />
							) : (
								<div className="table-scroll">
									<Table variant="resource">
										<TableHeader>
											<TableRow>
												<SortHeader className="wrap" label={t("table.page")} column="title" sort={sort} />
												<SortHeader className="num" label={t("reflowPages.live")} column="live" sort={sort} />
												<SortHeader className="num" label={t("reflowPages.before")} column="before" sort={sort} />
												<SortHeader className="num" label={t("reflowPages.since")} column="since" sort={sort} />
												<TableHead>{t("reflowPages.conclusion")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{rows.map((row) => (
												<TableRow key={row.page_id}>
													<TableCell className="wrap">
														<DrillLink to={`/w/${entityId}/pages/${row.page_id}`}>{row.title}</DrillLink>
														<div className="faint">
															{row.kind} · {fmt.date(locale, row.published_at)}
														</div>
													</TableCell>
													<TableCell className="num mono">{t("reflowPages.days", { n: row.days_live })}</TableCell>
													<TableCell className="num mono">{row.cited_before}</TableCell>
													<TableCell className="num mono">
														{/* Both counts are of this page's citations, so the
														    one worth opening is the one since it went live. */}
														<Drill view={{ kind: "citations", label: row.title, filters: { page: row.page_id } }}>
															{row.cited_since}
														</Drill>
													</TableCell>
													<TableCell>
														<Conclusion row={row} entityId={entityId} />
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</>
					);
				}}
			</Async>
		</StageShell>
	);
}

/**
 * What the reports screen says about this page, or why it says nothing yet.
 *
 * Three states and not two: measured and significant, measured and not, and
 * not yet measured. Collapsing the last two would tell somebody a page did
 * nothing when the truth is that nobody has looked.
 */
function Conclusion({ row, entityId }: { row: PageStanding; entityId: string }) {
	const t = useT();
	const { locale } = useI18n();

	if (row.reflow_id === null) {
		const left = Math.max(0, REFLOW_DAYS - row.days_live);
		return <span className="faint">{left > 0 ? t("reflowPages.waiting", { n: left }) : t("reflowPages.due")}</span>;
	}

	if (!row.significant || row.delta === null) {
		return (
			<DrillLink to={`/w/${entityId}/reflow/reflows`}>
				<span className="pill">{t("reflow.notSignificant")}</span>
			</DrillLink>
		);
	}

	const delta = row.delta;
	return (
		<DrillLink to={`/w/${entityId}/reflow/reflows`}>
			<span className={delta >= 0 ? "pill pill-success" : "pill pill-danger"}>
				{delta >= 0 ? "▲" : "▼"} {fmt.signed(locale, delta)}
			</span>
			<ChevronRight aria-hidden />
		</DrillLink>
	);
}

function csv(rows: PageStanding[]): (string | number)[][] {
	return [
		["title", "kind", "published_at", "days_live", "cited_before", "cited_since", "significant", "delta"],
		...rows.map((row) => [
			row.title,
			row.kind,
			row.published_at,
			row.days_live,
			row.cited_before,
			row.cited_since,
			row.reflow_id === null ? "" : String(row.significant),
			row.delta === null ? "" : row.delta.toFixed(2),
		]),
	];
}
